/**
 * Shared utilities for drilldown modals.
 *
 * Consolidates common functions used across DrilldownModal,
 * OTDDrilldownModal, and OTIFDrilldownModal.
 */

import { ExternalLink } from 'lucide-react';
import { statusOf, type LineStatusFields } from '../purchasing/lineStatus';
import React from 'react';
import { RECEIPT_STATUS_LABELS } from '../purchasing/receiptStatus';
import { poHeaderHref, poNumberHref } from '../common/PoHeaderLink';

// =============================================================================
// TYPES
// =============================================================================

export interface LinkableFieldConfig {
  urlPattern: (value: string, record: Record<string, unknown>) => string;
  tab?: string;
}

export interface TabConfig {
  key: string;
  label: string;
  filter: Record<string, string>;
}

export interface DrilldownField {
  field: string;
  label: string;
}

// =============================================================================
// LINKABLE FIELDS CONFIGURATION
// =============================================================================

/**
 * Common linkable fields shared across all drilldown modals
 */
export const COMMON_LINKABLE_FIELDS: Record<string, LinkableFieldConfig> = {
  grn_number: {
    urlPattern: (value) => `/purchasing?tab=delivery&search=${encodeURIComponent(value)}`,
    tab: 'delivery',
  },
  po_line__po__po_number: {
    // CR-7278: the PO number opens its line's detail when the row is one line,
    // else the PO header.
    urlPattern: (value, record) => poNumberHref(value, record['po_line_id']),
    tab: 'po_lines',
  },
  po_line__line_number: {
    // CR-1154: deep-link to the PO line item detail via its PK. Never key this
    // on `record.id` — that is the metric's base-model PK (GRN / NCR / appeal),
    // so the backend emits an explicit `po_line_id`. Falls back to the
    // PO-filtered list when a record has no resolvable line (e.g. an NCR whose
    // import never matched one).
    urlPattern: (_value, record) => {
      const poLineId = record['po_line_id'];
      if (poLineId != null) {
        return `/purchasing?tab=po_lines&line=${String(poLineId)}`;
      }
      const poNumber = String(record['po_line__po__po_number'] ?? '');
      return `/purchasing?tab=po_lines&search=${encodeURIComponent(poNumber)}`;
    },
    tab: 'po_lines',
  },
  // CR-7278: PO number fields open the row's line, else the PO header.
  po__po_number: {
    urlPattern: (value, record) => poNumberHref(value, record['po_line_id']),
    tab: 'po_lines',
  },
  po_number: {
    urlPattern: (value, record) => poNumberHref(value, record['po_line_id']),
    tab: 'po_lines',
  },
  grn__po_line__po__po_number: {
    urlPattern: (value, record) => poNumberHref(value, record['po_line_id']),
    tab: 'po_lines',
  },
  ncr_number: {
    urlPattern: (value) => `/quality?tab=ncrs&search=${encodeURIComponent(value)}`,
    tab: 'ncrs',
  },
};

/**
 * Build a deep-link URL to the PO Lines tab for an OTIF drill-down row.
 *
 * CR-4666: Pass `line=<id>` so POLinesTab opens the exact line (was falling
 * back to the first line of the PO).
 * CR-4667: Also pass `supplier=<id>` so the list filter can disambiguate
 * POs whose number collides across different suppliers. Without the
 * supplier id, `po__po_number__icontains=<number>` can resolve to a
 * different PO than the one the user clicked in the drill-down.
 */
function buildOtifPoLinesUrl(record: Record<string, unknown>, poNumber?: string): string {
  const params = new URLSearchParams({ tab: 'po_lines' });
  if (poNumber) params.set('search', poNumber);
  const lineId = String(record['id'] ?? '');
  if (lineId) params.set('line', lineId);
  const supplierId = String(record['po_supplier_id'] ?? '');
  if (supplierId) params.set('supplier', supplierId);
  return `/purchasing?${params.toString()}`;
}

/**
 * OTIF-specific linkable fields
 */
export const OTIF_LINKABLE_FIELDS: Record<string, LinkableFieldConfig> = {
  ...COMMON_LINKABLE_FIELDS,
  line_number: {
    urlPattern: (_value, record) => {
      const poNumber = String(record['po__po_number'] || record['po_line__po__po_number'] || '');
      return buildOtifPoLinesUrl(record, poNumber);
    },
    tab: 'po_lines',
  },
  po__po_number: {
    urlPattern: (value, record) => buildOtifPoLinesUrl(record, String(value || '')),
    tab: 'po_lines',
  },
};

// =============================================================================
// ROW DRILL-THROUGH (CR-7276)
// =============================================================================

/**
 * Where a whole drill-down ROW opens. The individual cells have carried links
 * for a while (PO number → header, line number → line), but the row itself
 * only highlighted on hover and did nothing on click, so a user who clicked a
 * failed line — rather than the small link inside it — got nothing.
 *
 * Priority: the PO line's GRN tab (the receipts are what an investigator is
 * reaching for), then the NCR, then the PO header (CR-7278). `null` means the row has
 * nothing to open and must render with no affordance at all.
 *
 * Never key on `record.id` for the PO line: it is the metric's base-model PK
 * (GRN / NCR / appeal / line) and differs per metric — the backend emits an
 * explicit `po_line_id` for exactly this reason.
 */
export function getRowDrillUrl(record: Record<string, unknown>): string | null {
  const poLineId = record['po_line_id'];
  if (poLineId !== null && poLineId !== undefined && poLineId !== '') {
    return `/purchasing?tab=po_lines&line=${encodeURIComponent(String(poLineId))}&lineTab=grn`;
  }
  const ncrId = record['ncr_id'] ?? (record['ncr_number'] ? record['id'] : undefined);
  if (ncrId !== null && ncrId !== undefined && ncrId !== '') {
    return `/quality/ncr/${encodeURIComponent(String(ncrId))}/edit`;
  }
  const poNumber = record['po_number'] ?? record['po_line__po__po_number'] ?? record['po__po_number'];
  if (poNumber) {
    return poHeaderHref(String(poNumber));
  }
  return null;
}

/**
 * Opens a drill target exactly as the cell links do: through a real anchor
 * with target="_blank", so the browser treats it as a link the user clicked —
 * a focused new tab, never a popup to block — and the drill-down stays put.
 *
 * CR-7276 rework: this was `window.open(url, '_blank', 'noopener,noreferrer')`.
 * Chrome applies popup rules to window.open that it never applies to a link,
 * and could leave the new tab unfocused, so a row click looked like nothing
 * happened while the blue links in the same row worked.
 */
export function openDrillUrl(url: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/** Class to add to a row that has a drill target, so the cursor matches the behaviour. */
export const ROW_DRILL_CLASS = 'cursor-pointer';

/**
 * Props that make a table row a real control: click and keyboard (Enter /
 * Space) both open the target, and the row is reachable by Tab with a role
 * assistive tech understands. Returns nothing for a row with no target, so the
 * affordance and the behaviour can never disagree.
 */
export function rowDrillProps(url: string | null): React.HTMLAttributes<HTMLTableRowElement> {
  if (!url) return {};
  return {
    role: 'link',
    tabIndex: 0,
    title: url.includes('lineTab=grn') ? 'Open the PO line on its GRN tab' : 'Open record',
    onClick: () => openDrillUrl(url),
    onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openDrillUrl(url);
      }
    },
  };
}

// =============================================================================
// VALUE FORMATTING
// =============================================================================

/**
 * CR-7270: is this record described AS AT a frozen month's boundary? The server
 * sets `frozen_as_at` on exactly those rows (purchasing.services.frozen_arrears).
 */
export function isFrozenRecord(record: unknown): boolean {
  return Boolean((record as { frozen_as_at?: string | null } | null | undefined)?.frozen_as_at);
}

/**
 * What an ABSENT cell says — '-', on every row of every drill-down.
 *
 * The parameter is kept so the call sites (which pass the row) do not all have
 * to change, and so a future rule that DOES depend on the row has somewhere to
 * live.
 */
export function absentText(_record?: unknown): string {
  // CR-7270 REWORK (Aaron, 18 Sep 2026): ONE mark for absent, on every row of
  // every modal. The 15 Sep split — '-' live, 'N/A' frozen — was meant to say
  // "the month did not record this", but the customer read a screen of N/A as
  // broken, and it also fired on columns the capture never owned (Supplier
  // Group is a live descriptor: absent because that supplier has no group, not
  // because December forgot). A dash says "nothing here" and says it the same
  // way in all four tabs. The cached-data pill above the grid is what tells a
  // reader the rows are month-end data.
  return '-';
}

/**
 * Format a value for display in drilldown tables
 */
export function formatDrilldownValue(
  value: unknown,
  // CR-6307: pass the preference-aware formatter (useFormatters().formatDate)
  // so date cells honour the user's date_format with no day-shift. Falls back
  // to a neutral format only if no formatter is supplied.
  formatDate?: (date: string | Date) => string,
  // CR-7270: the row the value belongs to, so an absent value can say N/A on a
  // frozen row. Optional: callers formatting a bare value keep '-'.
  record?: unknown,
): string {
  if (value === null || value === undefined) return absentText(record);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  // Check if it's a date string (ISO format)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    if (formatDate) return formatDate(value);
    // eslint-disable-next-line no-restricted-syntax -- CR-6307: no-formatter fallback only
    return new Date(value).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  return String(value);
}

/**
 * Get status badge styling for OTD status
 */
export function getOTDStatusStyle(status: string): string {
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
  const styles: Record<string, string> = {
    on_time: 'bg-green-100 text-green-800',
    'on time': 'bg-green-100 text-green-800',
    early: 'bg-blue-100 text-blue-800',
    late: 'bg-red-100 text-red-800',
  };
  return styles[normalizedStatus] || 'bg-gray-100 text-gray-800';
}

/**
 * The shape every OTIF verdict badge wears, whatever its colour. One constant,
 * because Pending, PASS and FAILED sit in the same column and any drift in
 * padding or radius between them shows up as a ragged edge (CR-7416).
 */
export const OTIF_BADGE_SHAPE = 'px-2 py-1 rounded text-xs font-medium';

/** The neutral tone, for a verdict that does not exist yet. */
export const OTIF_BADGE_NEUTRAL = 'bg-muted text-muted-foreground';

/** Which of the three states a verdict is in, without reading its words. */
export type OtifTone = 'pass' | 'fail' | 'none';

/**
 * The OUTLINED treatment of the same three states.
 *
 * CR-7416: needed because the PO Line modal header puts the verdict next to the
 * pale-green "% Delivered" pill, and two near-identical green chips side by
 * side is the exact thing Aaron rejected on CR-7418's header the same day —
 * the words were distinguishable and the eye still read one blob. Outlined
 * rather than filled makes it a different CLASS of chip at a glance while
 * keeping green meaning pass, and it borrows CR-7418's own "outlined, never
 * filled" precedent from that header rather than inventing a treatment.
 *
 * Keyed on the tone, never on the label, so renaming PASS to PASSED cannot
 * silently drop a cell back to a default colour.
 */
export const OTIF_BADGE_OUTLINE: Record<OtifTone, string> = {
  pass: 'border border-green-300 text-green-800 bg-transparent',
  fail: 'border border-red-300 text-red-800 bg-transparent',
  none: 'border border-border text-muted-foreground bg-transparent',
};

/**
 * Get status badge styling for OTIF status
 */
export function getOTIFStatusStyle(
  value: unknown,
  lineStatus?: unknown,
): { className: string; label: string; judged: boolean; tone: OtifTone } {
  // METRIC-DEFINITIONS wave 2/3a (R-131): the verdict is three-state —
  // true, false, or NULL for no verdict yet.
  //
  // CR-7270: this column is the VERDICT, so it prints verdicts and nothing
  // else. It used to print 'OPEN' for a NULL on a pending line, which is not
  // an OTIF status at all — it is the LINE's lifecycle, and the Line Status
  // column beside it already says exactly that. On a frozen month's arrears
  // rows, where nothing is EVER judged, the column claimed a status the month
  // never had. No verdict reads N/A (Aaron, 15 Sep: the longer wording was
  // overkill), and the line's own status stays in its own column.
  // CR-7270 REWORK (Aaron, 18 Sep 2026). This function used to read the value
  // ALONE: true was a PASS and EVERYTHING else that was not null was a FAILED.
  // A line is not judged until it closes, so `false` on an OPEN line is not a
  // failure — it is a verdict the line has not reached. Gardner carries 41
  // such rows live (partially_received, is_otif=false, otif_status='short'):
  // judged closed on 7 Sep, reopened by an import on 13 Sep when a receipt was
  // revised down from 9 to 8, and nothing cleared the verdict. They print
  // FAILED on every surface that shows this column, and the frozen arrears
  // rows would have joined them wholesale.
  //
  // So the cell asks the LINE, not just the flag. `lineStatus` omitted keeps
  // the old value-only reading for a caller with no row to hand.
  const closed = lineStatus === undefined
    || String(lineStatus ?? '').toLowerCase() === 'received';
  const hasVerdict = !(value === null || value === undefined || value === ''
    || value === 'None' || value === 'Not judged');
  if (!hasVerdict || !closed) {
    // "Pending", not "N/A" (Aaron, 18 Sep, superseding his own 15 Sep wording):
    // N/A reads as EXCLUDED — the customer took it for a broken screen — while
    // Pending says the line is still open and has not reached a verdict.
    //
    // CR-7416 (Aaron, 19 Sep): *"need to correct the pending status badge so it
    // matches the style of the pass and fail"* — which REVERSES his own 18 Sep
    // ruling that it be plain text. It reads as a broken cell beside two
    // badges, so all three are badges now and the hierarchy is carried by
    // COLOUR instead: PASS and FAILED keep the green and the red, Pending takes
    // the system's neutral, so a real verdict still wins the eye and an absent
    // one no longer looks like a rendering fault.
    return { className: OTIF_BADGE_NEUTRAL, label: 'Pending', judged: false, tone: 'none' };
  }
  // CR-4543: Match actual DB values — 'otif' and 'early' = pass, others = fail.
  // 'PASS' stays in this list because it is an INPUT the API can send, not the
  // word we print.
  const isPass = value === true || value === 'True' || value === 'PASS' || value === 'otif' || value === 'early';
  return {
    className: isPass
      ? 'bg-green-100 text-green-800 hover:bg-green-200'
      : 'bg-red-100 text-red-800 hover:bg-red-200',
    // "PASSED", not "PASS" (Aaron, 19 Sep 2026, looking at the modal header:
    // *"the badge should read OTIF PASSED"*). It also ends a tense mismatch
    // that had been on screen since CR-4543 — the same cell read "PASS" beside
    // "FAILED", one present and one past, about the same finished judgment.
    label: isPass ? 'PASSED' : 'FAILED',
    judged: true,
    tone: isPass ? 'pass' : 'fail',
  };
}

/**
 * Get styled label for GRN on_time_status field
 */
export function getOnTimeStatusStyle(value: unknown): { className: string; label: string } {
  const strValue = String(value || '').toLowerCase();
  switch (strValue) {
    case 'on_time':
      return { className: 'bg-green-100 text-green-800', label: 'On Time' };
    case 'early':
      return { className: 'bg-blue-100 text-blue-800', label: 'Early' };
    case 'late':
      return { className: 'bg-red-100 text-red-800', label: 'Late' };
    // CR-6518: overridden verdicts are native statuses, not a flag beside the
    // status. They read green because they score as a pass, but name the
    // verdict they replaced so an accepted exception stays distinguishable
    // from a delivery that met its date on its own merits.
    case 'overridden_early':
      return { className: 'bg-green-100 text-green-800', label: 'Overridden (Early)' };
    case 'overridden_late':
      return { className: 'bg-green-100 text-green-800', label: 'Overridden (Late)' };
    default:
      return { className: 'bg-gray-100 text-gray-800', label: value ? String(value) : '-' };
  }
}

// =============================================================================
// CELL RENDERING
// =============================================================================

export interface RenderCellOptions {
  fieldKey: string;
  value: unknown;
  record: Record<string, unknown>;
  linkableFields?: Record<string, LinkableFieldConfig>;
  onStatusClick?: (record: Record<string, unknown>) => void;
  statusField?: string;
}

/**
 * Create a link element for linkable fields.
 *
 * CR-1154: open every deep-link in a new tab (target="_blank"), per the
 * explicit "every hyperlink opens in a new tab" requirement. This reverses
 * CR-5363 round 2, which had made drill-down links same-tab after testers
 * reported the new tab "did nothing" (popup-blocked / opened behind the
 * drilldown). The ExternalLink icon signals the new-tab behaviour.
 */
export function createFieldLink(
  url: string,
  displayValue: string
): React.ReactElement {
  return React.createElement(
    'a',
    {
      href: url,
      target: '_blank',
      rel: 'noopener noreferrer',
      className: 'text-primary hover:underline inline-flex items-center gap-1 font-medium',
      onClick: (e: React.MouseEvent) => e.stopPropagation(),
    },
    displayValue,
    React.createElement(ExternalLink, { className: 'h-3 w-3' })
  );
}

/**
 * The PO line's status, as a badge, for any drill-down that shows the column.
 *
 * CR-7416 (Aaron, 19 Sep): *"Line status needs to be changed to status and show
 * the actual po line status, currently i think its the literal code variable
 * rather than the front end badge like wip, changed ect — important these
 * changes need to be accross all relevant modals that show these columns"*.
 *
 * Two different facts were both wearing the word "status" here. The column was
 * printing `PurchaseOrderLine.status`, the QUANTITY lifecycle (`pending`,
 * `partially_received`, `received`) — raw, lower-case, underscored. The badge
 * every other PO-line surface prints comes from `statusOf`, which reads
 * `acknowledge_status` and the hold fields. This makes the drill-downs ask the
 * same function as the grid and the line modal, so one line cannot read "New"
 * on one screen and "pending" on the next.
 *
 * The lifecycle has NOT been thrown away: it stays on the record under `status`
 * and the OTIF verdict beside this cell is still read from it (CR-7270 — a line
 * reaches no verdict until it closes).
 *
 * @param record - A drill-down row. The backend record serializer carries the
 *   acknowledge and hold fields on every PurchaseOrderLine row.
 */
export function createLineStatusBadge(
  record: Record<string, unknown>
): React.ReactElement {
  // CR-7270's rule, applied to this column: a row described AS AT a month end is
  // rendered from its capture, and the capture holds the LIFECYCLE and no
  // acknowledge status at all. Printing the live badge under the grid's own
  // "cached month-end data" banner would describe today, not the month.
  if (record['frozen_as_at']) {
    const captured = record['status'];
    // CR-7412's vocabulary on the captured word, matching the Supplier
    // Performance drill's own cell. Both are hand-written copies of one rule
    // and `lineStatusBadge.test` compares them row for row, which is how this
    // half was caught when only the other one was humanised.
    const word = captured
      ? (RECEIPT_STATUS_LABELS[String(captured)] ?? String(captured))
      : '-';
    return React.createElement(
      'span',
      { className: 'px-2 py-1 rounded text-xs font-medium border border-border text-muted-foreground' },
      word,
    );
  }
  const fields: LineStatusFields = {
    acknowledge_status: (record['acknowledge_status'] as string | null) ?? null,
    on_hold: record['on_hold'] === true || record['on_hold'] === 'True',
    on_hold_by_company_type: (record['on_hold_by_company_type'] as string | null) ?? null,
    hold_next_action_with: (record['hold_next_action_with'] as string | null) ?? null,
    on_hold_reason_text: (record['on_hold_reason_text'] as string | null) ?? null,
  };
  // A row carrying neither an acknowledge status nor a hold has nothing to say,
  // and `statusOf` would default it to "New" — a claim, on a row that made
  // none. One mark for any null, as every other drill-down cell uses (18 Sep).
  if (!fields.acknowledge_status && !fields.on_hold) {
    return React.createElement('span', { className: 'text-muted-foreground' }, '-');
  }
  const { label, className, title } = statusOf(fields);
  return React.createElement(
    'span',
    { className: `px-2 py-1 rounded text-xs font-medium ${className}`, title },
    label
  );
}

/**
 * Create an OTD status badge
 */
export function createOTDStatusBadge(value: string): React.ReactElement {
  // CR-7270 note: this badge renders a GRN's on-time verdict, whose absent
  // case is '-' (getOnTimeStatusStyle), and GRN rows are never described as at
  // a frozen boundary. The verdict rule lives on the PO-line
  // surfaces instead. Left alone deliberately.
  const { className, label } = getOnTimeStatusStyle(value);
  return React.createElement(
    'span',
    { className: `px-2 py-1 rounded text-xs font-medium ${className}` },
    label
  );
}

/**
 * Create an OTIF status button.
 *
 * DORMANT — not wired to any surface (no importer in frontend/src); see
 * CR-7270. Kept in step with the N/A rule so a future consumer inherits it
 * rather than re-introducing the badge.
 */
export function createOTIFStatusButton(
  value: unknown,
  onClick: () => void
): React.ReactElement {
  const { className, label } = getOTIFStatusStyle(value);
  return React.createElement(
    'button',
    {
      onClick,
      // CR-7416: every state carries the badge shape now — see
      // getOTIFStatusStyle. `judged` still says whether a verdict EXISTS; it
      // no longer decides whether the cell looks like a badge.
      className: `${OTIF_BADGE_SHAPE} ${className}`,
    },
    label
  );
}

// =============================================================================
// EXPORT HANDLING
// =============================================================================

/**
 * Download exported data as a file
 */
export function downloadExportedData(
  data: BlobPart,
  filename: string,
  format: 'csv' | 'xlsx'
): void {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${filename}.${format}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// =============================================================================
// TAB CONFIGURATIONS
// =============================================================================

export const OTD_TABS: TabConfig[] = [
  { key: 'on_time', label: 'ON TIME', filter: { otd_status: 'on_time' } },
  { key: 'early', label: 'EARLY', filter: { otd_status: 'early' } },
  { key: 'late', label: 'LATE', filter: { otd_status: 'late' } },
  { key: 'total', label: 'TOTAL', filter: {} },
];

// Mirrors the backend registry (drilldown/registry/metrics_delivery.py) — keep
// the two in step, the labels and filters are duplicated rather than fetched.
export const OTIF_TABS: TabConfig[] = [
  { key: 'failed', label: 'FAILED OTIF LINES', filter: { otif_status: 'failed' } },
  { key: 'arrears', label: 'ARREAR LINES', filter: { otif_status: 'arrears' } },
  { key: 'positive', label: 'PASSED OTIF LINES', filter: { otif_status: 'positive' } },
  // CR-6155: TOTAL excludes arrears, which the ARREAR tab already counts, so
  // TOTAL + ARREAR is the OTIF denominator instead of double-counting every
  // arrear. An empty filter here would show all lines and contradict the ring.
  { key: 'total', label: 'TOTAL LINES', filter: { otif_status: 'total' } },
];
